import {FormProvider, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import Button from 'components/Button';
import FormResult from 'components/Form/FormResult';
import Link from 'components/Link';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import SectionHeader from 'components/SectionHeader';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import usePaginatedData from 'hooks/usePaginatedData';
import {useBackend} from 'state/backend';
import {setUrl} from 'utils/request';
import {onApiError, onApiSuccess} from 'utils/request-axios/utils';
import useToggleTeamMemberGroup from './services';
import {TeamMemberGroupsBackendData} from './types';

const TeamMemberGroups = () => {
    const {
        data: {canEditTeam, groups = [], name, navigationTabs, teamMemberMid},
        setData,
    } = useBackend<TeamMemberGroupsBackendData>();

    const methods = useForm();

    const {buttonProps, isLoading, mutateAsync} =
        useToggleTeamMemberGroup(teamMemberMid);

    const {t} = useTranslation('Team.Member.Groups');

    const {paginatedData, ...pagination} = usePaginatedData(groups, 20);

    const handleToggleGroup = (groupMid: string) => {
        mutateAsync(groupMid, {groupMid})
            .then(
                onApiSuccess({
                    onRedirect: (response) => {
                        if (response.name === 'upgrade_groups') {
                            return setUrl(window.Urls.upgrade_groups());
                        }
                        throw new Error('Unexpected response');
                    },
                    onSuccess: (response) => {
                        if (response.name === 'refreshData' && response.data) {
                            return setData(response.data);
                        }
                        throw new Error('Unexpected response');
                    },
                }),
            )
            .catch(onApiError(methods));
    };

    return (
        <FormProvider {...methods}>
            <FormResult />
            <SectionHeader title={name} />
            <NavigationTabs.TeamMember
                className="my-4"
                mid={teamMemberMid}
                selection="groups"
                {...navigationTabs}
            />
            <p className="mt-6 mb-3">{t('subheader')}</p>
            <TableStatic className="mb-4" id="groups-table">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column className="whitespace-nowrap">
                            {t('groupName')}
                        </TableStatic.Column>
                        <TableStatic.Column className="text-center whitespace-nowrap">
                            {t('memberCount')}
                        </TableStatic.Column>
                        <TableStatic.Column className="text-center whitespace-nowrap">
                            {t('clientCount')}
                        </TableStatic.Column>
                        <TableStatic.Column />
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {paginatedData.length > 0 ? (
                        paginatedData.map(
                            (group) =>
                                group && (
                                    <TableStatic.Row key={group.mid}>
                                        <TableStatic.Cell>
                                            <Link
                                                href={window.Urls.team_item_view(
                                                    group.mid,
                                                )}
                                            >
                                                {group.name}
                                            </Link>
                                        </TableStatic.Cell>
                                        <TableStatic.Cell className="text-center whitespace-nowrap">
                                            {group.members?.length > 0
                                                ? group.members.length
                                                : undefined}
                                        </TableStatic.Cell>
                                        <TableStatic.Cell className="text-center whitespace-nowrap">
                                            {group.clientCount > 0 &&
                                                group.clientCount}
                                        </TableStatic.Cell>
                                        <TableStatic.Cell className="flex justify-end">
                                            <TooltipWrapper
                                                message={
                                                    canEditTeam.failureReason
                                                }
                                            >
                                                <Button
                                                    disabled={
                                                        !canEditTeam.allowed ||
                                                        isLoading
                                                    }
                                                    {...buttonProps[group.mid]}
                                                    kind="secondary"
                                                    onClick={() =>
                                                        handleToggleGroup(
                                                            group.mid,
                                                        )
                                                    }
                                                    size="xs"
                                                >
                                                    {group.members?.some(
                                                        (member) =>
                                                            member.mid ===
                                                            teamMemberMid,
                                                    )
                                                        ? t('removeGroup')
                                                        : t('addGroup')}
                                                </Button>
                                            </TooltipWrapper>
                                        </TableStatic.Cell>
                                    </TableStatic.Row>
                                ),
                        )
                    ) : (
                        <TableStatic.Row>
                            <TableStatic.Cell>
                                <Trans
                                    components={[
                                        <Link
                                            key={0}
                                            href={window.Urls.team_create_view(
                                                'group',
                                            )}
                                        />,
                                    ]}
                                    i18nKey="createGroup"
                                    ns="Team.Member.Groups"
                                    t={t}
                                />
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                </TableStatic.Body>
            </TableStatic>
            <TablePagination
                hideForSinglePage={true}
                id="groups-pagination"
                {...pagination}
            />
        </FormProvider>
    );
};

export default TeamMemberGroups;
